import type { FC } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { useEstimatedDeliveryMessage } from '../../../../hooks/useEstimatedDeliveryMessage';
import { Text } from '../../../../ui-components';
import { isGiftRegistry } from '../../../../utils/product-utils';
import { useBasketProductState } from '../../hooks/useBasketProductState';
import { BaseItem } from './BaseItem';
import { CartItemPfaWarning } from './CartItemPfaWarning';
import { GiftRegistryLink } from './GiftRegistryLink';
import { PromotionBadges } from './PromotionBadges';
import { SaveForLaterButton } from './SaveForLaterButton';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { Product } from '../../../../bff-api/products/model';
import type { BaseItemProps } from './BaseItem';

export type DropshipItemProps = {
    productItem: ProductItemEditor;
    product: Product;
    variation?: string;
} & Omit<BaseItemProps, 'id'>;

export const DropshipItem: FC<DropshipItemProps> = (props) => {
    const { productItem, variation } = props;
    const basketState = useBasketProductState();
    const productState = basketState.getStateForProduct(productItem.productId);

    const {
        c_dropshipEstimatedDeliveryDate,
        c_dropshipEstimatedProcessingDate,
        c_customDeliveryMessage,
    } = productState?.product ?? {};

    const now = Date.now();
    const { message: customMessage, startDate, endDate } = c_customDeliveryMessage ?? {};
    const showCustomDeliveryMessage =
        customMessage &&
        (startDate ? startDate <= now : false) &&
        (endDate ? now <= endDate : false);

    const dropshipMessage = useEstimatedDeliveryMessage({
        c_dropshipEstimatedDeliveryDate,
        c_dropshipEstimatedProcessingDate,
    });

    const isProductGiftRegistry = isGiftRegistry(productItem);

    return (
        <BaseItem
            {...props}
            id={`cart-item-dropship-${productItem.productId}`}
            middleSection={variation ? <Text>{variation}</Text> : null}
            footerSection={
                <Flex
                    data-testid="cart-item-footer-section"
                    width="100%"
                    flexDirection="column"
                    gap={6}
                    sx={{
                        ':empty': {
                            display: 'none',
                        },
                    }}
                >
                    {isProductGiftRegistry && <GiftRegistryLink productItem={productItem} />}

                    <Flex
                        backgroundColor="slt-grey.150"
                        padding={2}
                        borderWidth={1}
                        borderRadius="4px"
                        borderColor="slt-grey.1000"
                        direction="column"
                        width="100%"
                        gap={1}
                    >
                        <Text id="cart-item-dropship-title" style="bold">
                            <FormattedMessage
                                id="cart-item.dropship.title"
                                defaultMessage="Ships directly from vendor"
                            />
                        </Text>
                        <Text id="cart-item-dropship-message">
                            {showCustomDeliveryMessage ? customMessage : dropshipMessage}
                        </Text>
                    </Flex>

                    <Flex gap={2} direction="column" data-testid="promotion-block">
                        <PromotionBadges {...productItem} />

                        <ConditionalRender visibility={!!productItem.c_isExcludedFromPromotion}>
                            <Text
                                data-testid="cart-item-promotion-badge-excluded-from-promo"
                                color="slt-red-error"
                            >
                                <FormattedMessage
                                    id="cart-item.badge.excluded-from-promo"
                                    defaultMessage="Excluded From Promo"
                                />
                            </Text>
                        </ConditionalRender>
                    </Flex>
                    <CartItemPfaWarning productItem={productItem} />
                </Flex>
            }
            buttonSection={<SaveForLaterButton productItem={productItem} />}
        />
    );
};

DropshipItem.defaultProps = {
    variation: undefined,
};
